import React, { useEffect, useRef } from 'react';
import { useUi } from '../../context/UiContext';
import useActiveSection from '../../hooks/useActiveSection';
import { WEB_APP_LINK } from '../../data/links';

const Header = () => {
  const { scrollY, isMenuOpen, toggleMenu, closeMenu } = useUi();
  const isScrolled = scrollY > 10;
  const activeSection = useActiveSection();
  const hamburgerRef = useRef(null);
  const mobileMenuSheetRef = useRef(null);

  const navigateToHash = (href) => {
    const hash = (href || '').startsWith('#') ? href : `#${href}`;
    const targetId = hash.replace('#', '');

    // Hash setzen, damit URL/History korrekt sind.
    // (replaceState vermeidet unnötige History-Einträge beim wiederholten Navigieren.)
    if (typeof window !== 'undefined') {
      if (window.location.hash !== hash) {
        window.history.replaceState(null, '', hash);
      }
    }

    closeMenu();

    // Nach dem Schließen & dem nächsten Frame scrollen (verhindert Race mit Overlay/Body overflow).
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = document.getElementById(targetId);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  };

  const desktopNavItems = [
    { title: 'Product', href: '#product', id: 'product' },
    { title: 'Platform', href: '#platform', id: 'platform' },
    { title: 'Benefits', href: '#benefits', id: 'benefits' },
    { title: 'Services', href: '#services', id: 'services' },
    { title: 'Pricing', href: '#pricing', id: 'pricing' },
    { title: 'FAQ', href: '#faq', id: 'faq' },
    { title: 'About', href: '#about', id: 'about' }
  ];

  // Mobile Navigation Items - deckungsgleich mit Desktop
  const mobileNavItems = [
    { title: 'Product', href: '#product', id: 'product' },
    { title: 'Platform', href: '#platform', id: 'platform' },
    { title: 'Benefits', href: '#benefits', id: 'benefits' },
    { title: 'Services', href: '#services', id: 'services' },
    { title: 'Pricing', href: '#pricing', id: 'pricing' },
    { title: 'FAQ', href: '#faq', id: 'faq' },
    { title: 'About', href: '#about', id: 'about' }
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      const navMenu = document.querySelector('.nav-menu');
      const hamburger = document.querySelector('.hamburger');
      const mobileMenuSheet = mobileMenuSheetRef.current;

      if (isMenuOpen && navMenu && hamburger) {
        if (mobileMenuSheet && mobileMenuSheet.contains(event.target)) return;
        if (!navMenu.contains(event.target) && !hamburger.contains(event.target)) {
          closeMenu();
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen, closeMenu]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isMenuOpen) {
        closeMenu();
        hamburgerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMenuOpen, closeMenu]);

  return (
    <header className={isScrolled ? 'solid' : 'transparent'}>
      <nav>
        <a href="/" className="logo">
          <img
            src="/final-logos/badge.png"
            alt="Ferritico"
            className="logo-icon"
            style={{ width: '45px', height: '45px', objectFit: 'contain' }}
          />
          <img
            src={isScrolled ? "/final-logos/blue.png" : "/final-logos/white.png"}
            alt="Ferritico"
            className="logo-wordmark"
            style={{
              height: '40px',
              objectFit: 'contain',
              marginLeft: '8px'
            }}
          />
        </a>

        <ul className="nav-menu">
          {desktopNavItems.map((item, idx) => (
            <li key={idx}>
              <a
                href={item.href}
                onClick={closeMenu}
                className={activeSection === item.id ? 'active' : ''}
              >
                {item.title}
              </a>
            </li>
          ))}
        </ul>

        <div className="nav-cta-group">
          <a href={WEB_APP_LINK} className="nav-demo">Web App</a>
        </div>

        <button
          className={`hamburger ${isMenuOpen ? 'active' : ''}`}
          onClick={toggleMenu}
          aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isMenuOpen}
          ref={hamburgerRef}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

      </nav>

      {/* Modern Mobile Menu - Card-based Design */}
      <div className={`mobile-menu-overlay ${isMenuOpen ? 'active' : ''}`} onClick={() => closeMenu()}>
        <div className="mobile-menu-sheet" ref={mobileMenuSheetRef} onClick={(e) => e.stopPropagation()}>

          {/* Menu Header */}
          <div className="menu-header">
            <h2>Explore</h2>
          </div>

          {/* Navigation Cards */}
          <div className="nav-cards-container">
            {mobileNavItems.map((item, idx) => (
              <a
                key={idx}
                href={item.href}
                className={`nav-card ${activeSection === item.id ? 'active' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  navigateToHash(item.href);
                }}
              >
                <div className="nav-card-content">
                  <h3 className="nav-card-title">{item.title}</h3>
                  <div className="nav-card-icon">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" />
                    </svg>
                  </div>
                </div>
                {/* nav-card-indicator entfernt */}
              </a>
            ))}
          </div>

          {/* Compact CTA Section */}
          <div className="menu-cta-compact">
            <a
              href="#contact"
              className="cta-btn primary"
              onClick={(e) => {
                e.preventDefault();
                navigateToHash('#contact');
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M2.5 3A1.5 1.5 0 0 0 1 4.5v.793c0 .089.048.17.126.22l4.5 2.25a.5.5 0 0 0 .448 0l4.5-2.25A.251.251 0 0 0 11 5.293V4.5A1.5 1.5 0 0 0 9.5 3h-7zM11 6.707l-3.75 1.875a1.5 1.5 0 0 1-1.5 0L1 6.707V12.5A1.5 1.5 0 0 0 2.5 14h7a1.5 1.5 0 0 0 1.5-1.5V6.707z" />
              </svg>
              Contact Us
            </a>
            <a
              href={WEB_APP_LINK}
              className="cta-btn secondary"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                <path d="M6.271 5.055a.5.5 0 0 1 .52.26L7.5 7.3l.708-1.985a.5.5 0 0 1 .54-.26 3 3 0 1 1-2.477 0z" />
              </svg>
              Web App
            </a>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
